import { useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { updatePreviewDimention } from '../utils/UtilsFunction';
import {
  update_full_window_size,
  update_preview_type,
} from '../redux/Slice/windowTypeSlice';
import store from '../redux/store';
import { setLoading } from '../redux/Slice/globalStates';
import { replayPreviewMode } from '../utils/preview/replayPreviewMode';
import { findSceneObjectByEventAssetId, normalizeEventTrigger } from '../utils/eventAssetUtils';

const usePreviewResize = ({ completeObject, functionCall }) => {
  const {
    rendererNew,
    scene,
    typeData,
    raycaster,
    isTransformActive,
    mouse,
    projectId,
    camera,
  } = completeObject || {};
  const dispatch = useDispatch();
  const resizingRef = useRef(null);
  const { window_size, window_type } = useSelector((state) => state.window);
  const { allIntersectedObjects } = useSelector((state) => state.assets_object);

  const startResize = (axis, side) => (e) => {
    e.preventDefault();

    resizingRef.current = {
      axis,
      side,
      startX: e.clientX,
      startY: e.clientY,
      initialWidth: window_size.width,
      initialHeight: window_size.height,
    };

    const onMouseMove = (moveEvent) => {
      const { axis, side, startX, startY, initialWidth, initialHeight } =
        resizingRef.current;

      const dx = moveEvent.clientX - startX;
      const dy = moveEvent.clientY - startY;

      let newWidth = initialWidth;
      let newHeight = initialHeight;

      if (axis === 'width') {
        const rawWidth =
          side === 'left' ? initialWidth - dx : initialWidth + dx;
        newWidth = Math.max(100, Math.min(rawWidth, window.innerWidth));
      }

      if (axis === 'height') {
        const rawHeight =
          side === 'top' ? initialHeight - dy : initialHeight + dy;
        newHeight = Math.max(100, Math.min(rawHeight, window.innerHeight));
      }

      let previewType = 'default';

      if (newWidth <= 500) {
        // ⬅️ 0-500px = Mobile
        previewType = 'mobile';
      } else if (newWidth <= 800) {
        // ⬅️ 501-800px = Tab
        previewType = 'tab';
      } else {
        // ⬅️ 801+ = Default
        previewType = 'default';
      }

      // Update preview type in Redux
      store.dispatch(update_preview_type(previewType));

      updatePreviewDimention({ width: newWidth, height: newHeight });
      dispatch(update_full_window_size({ width: newWidth, height: newHeight }));
    };

    const onMouseUp = () => {
      resizingRef.current = null;
      window.removeEventListener('mousemove', onMouseMove);
      window.removeEventListener('mouseup', onMouseUp);
    };

    window.addEventListener('mousemove', onMouseMove);
    window.addEventListener('mouseup', onMouseUp);
  };

  const doubleClick = (event) => {
    eventHandleFunction(event, 'Double Click');
  };
  const singleClick = (event) => {
    eventHandleFunction(event, 'Single Click');
  };

  const singleTouch = () => {};
  const doubleTouch = () => {};

  const clickTimer = useRef(null);

  const handleClick = (e) => {
    // Cancel any existing timer first to be safe
    if (clickTimer.current) clearTimeout(clickTimer.current);

    // Store only what you need from the event
    const eventData = {
      clientX: e.clientX,
      clientY: e.clientY,
      target: e.target,
      preventDefault: e.preventDefault,
      stopPropagation: e.stopPropagation,
    };

    clickTimer.current = setTimeout(() => {
      clickTimer.current = null;
      if (functionCall) {
        functionCall();
      }

      if (window_type === 'preview' || typeData == 'preview_page') {
        singleClick(eventData); // pass extracted event data
      }
    }, 250); // 250ms is ideal delay
  };

  const handleDoubleClick = (e) => {
    if (clickTimer.current) {
      clearTimeout(clickTimer.current);
      clickTimer.current = null;
    }

    if (window_type === 'preview') {
      doubleClick(e); // still pass full event here if needed
    }
  };

  const eventHandleFunction = (event, type) => {
    event.preventDefault();
    event.stopPropagation();

    if (isTransformActive) return;

    // Step 1: Get canvas bounds
    const rect = rendererNew?.domElement.getBoundingClientRect();

    if (!rect) return;
    // Step 2: Normalize mouse coordinates (-1 to 1)
    mouse.current.x = ((event.clientX - rect.left) / rect.width) * 2 - 1;
    mouse.current.y = -((event.clientY - rect.top) / rect.height) * 2 + 1;

    // Step 3: Raycast from camera

    raycaster.current.setFromCamera(mouse.current, camera);

    // Step 4: Intersect with all pickable objects
    const intersected = raycaster.current.intersectObjects(
      //   store.getState().assets_object.allIntersectedObjects,
      allIntersectedObjects,
      true
    );

    if (intersected.length > 0) {
      let obj = intersected[0].object;

      // Step 1: Get the top-level object under scene
      while (obj.parent && obj.parent !== scene) {
        obj = obj.parent;
      }

      const events = obj.events || obj?.userData?.events || [];

      // Step 2: Find matching event with "Double Click"
      const matchedEvent = events.find(
        (ev) =>
          normalizeEventTrigger(ev.trigger) === normalizeEventTrigger(type) &&
          ev.inputType === 'Mouse'
      );
      if (matchedEvent) {
        handleSceneObjectEventAction(matchedEvent, scene);
      }
    }
  };

  const handleReplay = () => {
    replayPreviewMode({
      scene,
      stopAllAnimations: completeObject?.stopAllAnimations,
      startAllAnimations:
        completeObject?.restartAnimations || completeObject?.startAllAnimations,
      renderer: rendererNew,
      camera,
    });
  };

  const handleUploadShare = async () => {
    try {
      store.dispatch(setLoading(true));
      //  const sceneData = scene.toJSON(); // serialize the full scene
      const sceneData = { ...scene };
      const obj = {
        scene: JSON.stringify(sceneData), // don't stringify yet
        id: projectId,
      };

      const response = await fetch(
        `${process.env.REACT_APP_BASE_URL}scene-preview`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify(obj),
        }
      );
      const result = await response.json();
      store.dispatch(setLoading(false));
      return result;
    } catch (error) {
      console.error('❌ Error uploading scene:', error);
    } finally {
      store.dispatch(setLoading(false));
    }
  };

  const handleFetchPreviewScene = async (id) => {
    try {
      const response = await fetch(
        `${process.env.REACT_APP_BASE_URL}scene-preview/${id}`,
        {
          method: 'GET',
        }
      );
      const result = await response.json();
      return result;
    } catch (error) {
      console.error('❌ Error uploading scene:', error);
      return { success: false, error: error };
    }
  };
  return {
    startResize,
    handleDoubleClick,
    handleClick,
    singleTouch,
    doubleTouch,
    handleReplay,
    handleUploadShare,
    handleFetchPreviewScene,
  };
};
// utils/sceneEvents.js or any reusable file
export const handleSceneObjectEventAction = (matchedEvent, scene) => {
  const assetsData = store.getState().assets_object?.assets_data || {};
  const targetObj = findSceneObjectByEventAssetId(
    scene,
    matchedEvent.affectedAsset,
    assetsData
  );

  switch (matchedEvent.action) {
    case 'Disable':
      if (targetObj) {
        targetObj.visible = false;
      } else {
        console.warn(
          `⚠️ Target object with id ${matchedEvent.affectedAsset} not found`
        );
      }
      break;

    case 'Enable':
      if (targetObj) {
        // Always show — including assets authored as hidden (eye off),
        // so Enable events can reveal them on interaction.
        targetObj.visible = true;
      } else {
        console.warn(
          `⚠️ Target object with id ${matchedEvent.affectedAsset} not found`
        );
      }
      break;

    case 'Redirect':
    case 'Redirect to URL':
      if (matchedEvent.url) {
        window.open(matchedEvent.url, '_blank');
      } else {
        console.warn('⚠️ No link provided in matchedEvent');
      }
      break;

    default:
      console.warn(`⚠️ Unknown action type: ${matchedEvent.action}`);
  }
};

export default usePreviewResize;
